import { oidcAuthService, type CognitoUser } from '@/services/OIDCAuthService';
import { useAuthStore } from '@/stores/auth';
import storage from '@/utils/storage';
import type { User } from 'oidc-client-ts';
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';

/**
 * Vue 3 Cognito OIDC 認証 Composable
 * 公式ドキュメントのoidc-client-ts実装に基づく
 */
export function useOIDCAuth() {
  const authStore = useAuthStore();
  const router = useRouter();

  const loading = ref(false);
  const error = ref<string | null>(null);

  // 認証状態の計算プロパティ
  const isAuthenticated = computed(() => !!authStore.token);
  const userInfo = computed(() => authStore.userInfo);
  const originalUrl = ref<string | null>(null);

  /**
   * エラーをクリア
   */
  const clearError = () => {
    error.value = null;
  };

  /**
   * ローディング状態を設定
   */
  const setLoading = (value: boolean) => {
    loading.value = value;
  };

  /**
   * エラーハンドリング
   */
  const handleError = (err: any) => {
    console.error('OIDC Auth Error:', err);
    error.value = err.message || '認証エラー';
    setLoading(false);
  };

  /**
   * 認証状態をstoreに更新
   */
  const updateAuthStore = async (user: User) => {
    try {
      // ユーザー情報/Tokenを更新
      const userProfile = {
        employeeId: (user.profile['custom:userId'] as string) || (user.profile['custom:employeeId'] as string) || '',
        idToken: user.id_token,
      };

      // カスタム属性を取得できたかチェック
      if (!userProfile.employeeId) {
        console.warn(
          '警告: カスタム属性を取得できませんでした。AWS Cognitoアプリケーションクライアントの属性読み取り権限設定を確認してください。',
        );
      }

      authStore.updateUserInfo(userProfile);
    } catch (err) {
      console.error('認証状態の更新に失敗:', err);
    }
  };

  /**
   * ログイン - 公式ドキュメントの例に基づく
   */
  const signIn = async () => {
    try {
      clearError();
      setLoading(true);
      // ログインリダイレクトを開始
      await oidcAuthService.signIn();
    } catch (err) {
      handleError(err);
      throw err;
    }
  };

  /**
   * ログインコールバックを処理 - 公式ドキュメントの例に基づく
   */
  const handleSigninCallback = async (): Promise<User | null> => {
    try {
      clearError();
      setLoading(true);

      const user = await oidcAuthService.handleSigninCallback();
      if (user) {
        await updateAuthStore(user);
      }

      setLoading(false);
      return user;
    } catch (err) {
      handleError(err);
      throw err;
    }
  };

  /**
   * ログアウト - 公式ドキュメントの例に基づく
   */
  const signOut = async () => {
    try {
      clearError();
      setLoading(true);

      // ローカル状態をクリア
      await authStore.logout();
      //OIDCユーザー状態をクリア
      await oidcAuthService.removeUser();
      // Cognitoログアウトにリダイレクト
      await oidcAuthService.signOut();
    } catch (err) {
      handleError(err);
      throw err;
    }
  };

  /**
   * ログアウトコールバックを処理
   */
  const handleSignoutCallback = async () => {
    try {
      clearError();
      setLoading(true);

      await oidcAuthService.handleSignoutCallback();
      setLoading(false);

      // ホームページにリダイレクト
      router.push('/');
    } catch (err) {
      handleError(err);
      throw err;
    }
  };

  /**
   * 認証状態をチェック - スマートな認証戦略
   */
  const checkAuthStatus = async (): Promise<boolean> => {
    try {
      setLoading(true);
      // 1. OIDCユーザー状態をチェック
      const user = await oidcAuthService.getCurrentUser();

      if (user && !user.expired) {
        console.log('OIDCユーザー有効、アプリケーション認証状態をチェック');

        // 2. アプリケーションレベルの認証状態をチェック
        const authStore = await import('@/stores/auth').then((m) => m.useAuthStore());
        authStore.restoreFromStorage();

        // アプリケーションレベルで認証済みの場合
        if (authStore.userInfo.employeeName && authStore.token) {
          console.log('アプリケーション認証も有効、認証成功');
          await updateAuthStore(user);
          return true;
        }

        // OIDC認証はあるが、アプリケーション認証がない場合
        // （新しいタブ/ウィンドウでsessionStorageが空の場合）
        console.log('OIDC認証有効だが、アプリケーション認証が必要');

        // OIDCユーザー情報を使ってアプリケーション状態を復元
        await updateAuthStore(user);

        // 復元後に再度チェック
        authStore.restoreFromStorage();
        if (authStore.userInfo.employeeName && authStore.token) {
          console.log('アプリケーション状態の復元成功');
          return true;
        }

        console.log('アプリケーション状態の復元が不完全、LoginPageで追加認証が必要');
        return false; // LoginPageで処理続行
      }

      console.log('OIDCユーザー無効または期限切れ');
      return false;
    } catch (error) {
      console.error('認証状態チェックエラー:', error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  /**
   * 現在のユーザー情報を取得
   */
  const getCurrentUserInfo = async (): Promise<CognitoUser | null> => {
    try {
      clearError();
      return await oidcAuthService.getUserInfo();
    } catch (err) {
      handleError(err);
      return null;
    }
  };

  /**
   * IDトークンを取得
   */
  const getIdToken = async (): Promise<string | null> => {
    try {
      clearError();
      return await oidcAuthService.getIdToken();
    } catch (err) {
      handleError(err);
      return null;
    }
  };

  /**
   * 認証状態を初期化
   */
  const initializeAuth = async () => {
    try {
      clearError();

      // 期限切れ状態をクリーンアップ
      await oidcAuthService.clearStaleState();

      // 現在の認証状態をチェック
      await checkAuthStatus();
    } catch (err) {
      console.warn('認証状態の初期化に失敗:', err);
    }
  };

  /**
   * OIDCユーザー状態をクリア
   */
  const clearOIDCUser = async () => {
    try {
      await oidcAuthService.removeUser();
      console.log('OIDCユーザー状態をクリアしました');
    } catch (err) {
      console.warn('OIDCユーザー状態のクリアに失敗しました:', err);
    }
  };

  /**
   * 認証前のURLを保存
   * @param url 保存するURL
   */
  const saveOriginalUrl = (url?: string) => {
    const urlToSave = url || window.location.href;

    originalUrl.value = urlToSave;
    // SessionStorageにも保存してブラウザーリフレッシュに対応
    // URLは文字列として直接保存（JSON.stringifyを使わない）
    storage.setItem('pxp_original_url', urlToSave);
  };

  /**
   * 保存された認証前のURLを取得
   * @returns 保存されたURL、なければnull
   */
  const getOriginalUrl = (): string | null => {
    // まずメモリから取得を試行
    if (originalUrl.value) {
      return originalUrl.value;
    }
    // SessionStorageから取得を試行
    const savedUrl = storage.getItem('pxp_original_url');
    if (savedUrl) {
      originalUrl.value = savedUrl;
      return savedUrl;
    }
    return null;
  };

  /**
   * 保存されたURLをクリア
   */
  const clearOriginalUrl = (): void => {
    originalUrl.value = null;
    storage.removeItem('pxp_original_url');
  };

  return {
    // 状態
    loading: computed(() => loading.value),
    error: computed(() => error.value),
    isAuthenticated,
    userInfo,
    // メソッド
    signIn,
    signOut,
    handleSigninCallback,
    handleSignoutCallback,
    checkAuthStatus,
    getCurrentUserInfo,
    getIdToken,
    initializeAuth,
    clearError,
    clearOIDCUser,
    saveOriginalUrl,
    getOriginalUrl,
    clearOriginalUrl,
  };
}
